import React, { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { Loader2, TrendingUp } from "lucide-react";
import { fetchAdminDailyStats } from "@/lib/api";

const fmtDay = (d) => new Date(`${d}T00:00:00+05:30`).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });

// Daily recorded ad events (IST days) from the database; zero means nothing recorded that day.
export default function AdminStatsTrend() {
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setD(null); setErr(false); fetchAdminDailyStats(days).then(setD).catch(() => setErr(true)); }, [days]);
  const tot = (k) => (d ? d.series.reduce((a, r) => a + r[k], 0) : 0);
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 min-w-0" data-testid="admin-stats-trend">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-700" /> Daily ad activity</h2>
        <div className="inline-flex rounded-full border border-slate-200 p-0.5" data-testid="admin-stats-trend-period">
          {[7, 30].map((n) => (
            <button key={n} onClick={() => setDays(n)} className={`px-3 py-1 text-xs font-semibold rounded-full min-h-[32px] transition-colors ${days === n ? "bg-emerald-700 text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`admin-stats-trend-${n}d`}>Last {n} days</button>
          ))}
        </div>
      </div>
      {err ? <div className="text-sm text-rose-700" data-testid="admin-stats-trend-error">Data unavailable — could not load daily stats.</div> : !d ? (
        <div className="text-sm text-slate-500 inline-flex items-center gap-2 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="text-xs text-slate-600 mb-2 break-words" data-testid="admin-stats-trend-label">
            Showing {fmtDay(d.from)} – {fmtDay(d.to)} (IST, last {d.days} days) · Views <b data-testid="admin-stats-trend-total-views">{tot("views")}</b> · Clicks <b data-testid="admin-stats-trend-total-clicks">{tot("clicks")}</b> · WhatsApp clicks <b data-testid="admin-stats-trend-total-wa">{tot("whatsapp_clicks")}</b>
          </div>
          <div className="h-64 w-full" data-testid="admin-stats-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.series} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11 }} minTickGap={12} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={fmtDay} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="linear" dataKey="views" name="Views" stroke="#047857" strokeWidth={2} dot={{ r: 2.5 }} />
                <Line type="linear" dataKey="clicks" name="Clicks" stroke="#d97706" strokeWidth={2} dot={{ r: 2.5 }} />
                <Line type="linear" dataKey="whatsapp_clicks" name="WhatsApp clicks" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 2.5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {d.views_tracking_since && <div className="text-[11px] text-slate-500 mt-1">Genuine view tracking started {new Date(d.views_tracking_since).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })}; earlier days show 0 views.</div>}
        </>
      )}
    </div>
  );
}
